import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Package, FileText, BookOpen, Phone, Inbox, Plus, Pencil, Trash2, X, Upload, Save, Loader2,
} from "lucide-react";
import { api, apiErr, assetUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "products", label: "Products", icon: Package },
  { key: "about_us", label: "About Us", icon: FileText },
  { key: "about_homeopathy", label: "About Homeopathy", icon: BookOpen },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "messages", label: "Enquiries", icon: Inbox },
];

/* -------------------- IMAGE UPLOAD FIELD -------------------- */
function ImageField({ value, onChange, label = "Product Image" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiErr(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-muted-foreground">{label}</label>
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-border bg-secondary">
          {value ? <img src={assetUrl(value)} alt="preview" className="h-full w-full object-cover" /> : null}
        </div>
        <div className="flex-1 space-y-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="btn-outline !py-2 !px-4" data-testid="admin-upload-image-button" disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="admin-image-file-input" />
          <input className="input-field" placeholder="or paste image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="admin-image-url-input" />
        </div>
      </div>
    </div>
  );
}

/* -------------------- PRODUCTS -------------------- */
const emptyProduct = { name: "", category: "", description: "", price: "", image_url: "", featured: 1, dosage: "", benefits: "" };

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null); // null | product object being edited/created
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setModal({ ...emptyProduct });
  const openEdit = (p) => setModal({ ...p });

  const save = async (e) => {
    e.preventDefault();
    if (!modal.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (modal.id) {
        await api.put(`/products/${modal.id}`, modal);
        toast.success("Product updated");
      } else {
        await api.post("/products", modal);
        toast.success("Product added");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(apiErr(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(apiErr(err, "Delete failed"));
    }
  };

  return (
    <div data-testid="admin-products-panel">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl">Products</h2>
          <p className="text-sm text-muted-foreground">{products.length} product(s)</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="admin-add-product-button">
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <div key={p.id} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[4/3] overflow-hidden bg-secondary">
              {p.image_url && <img src={assetUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" />}
            </div>
            <div className="flex flex-1 flex-col p-4">
              <div className="text-xs text-accent">{p.category}</div>
              <h3 className="font-serif text-lg">{p.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
              <div className="mt-auto flex gap-2 pt-3">
                <button onClick={() => openEdit(p)} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={`admin-edit-product-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-full border border-destructive/30 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10" data-testid={`admin-delete-product-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="admin-product-modal" onClick={() => !saving && setModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-card p-8 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl">{modal.id ? "Edit Product" : "Add Product"}</h3>
              <button type="button" onClick={() => setModal(null)} className="rounded-full p-1 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Product Name" placeholder="Product name" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="admin-product-name-input" />
              <Field label="Category" placeholder="e.g. Immunity" value={modal.category} onChange={(e) => setModal({ ...modal, category: e.target.value })} data-testid="admin-product-category-input" />
              <Field label="Price" placeholder="e.g. ₹199" value={modal.price} onChange={(e) => setModal({ ...modal, price: e.target.value })} data-testid="admin-product-price-input" />
              <Area label="Description" rows={4} placeholder="Description" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} data-testid="admin-product-description-input" />
              <Area label="Dosage & Directions" rows={3} placeholder="Shown on the product's detail page" value={modal.dosage || ""} onChange={(e) => setModal({ ...modal, dosage: e.target.value })} data-testid="admin-product-dosage-input" />
              <Area label="Key Benefits (one per line)" rows={4} placeholder={"Supports immunity\nGentle daily tonic"} value={modal.benefits || ""} onChange={(e) => setModal({ ...modal, benefits: e.target.value })} data-testid="admin-product-benefits-input" />
              <ImageField value={modal.image_url} onChange={(url) => setModal({ ...modal, image_url: url })} />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={!!modal.featured} onChange={(e) => setModal({ ...modal, featured: e.target.checked ? 1 : 0 })} data-testid="admin-product-featured-input" />
                Show on home page (featured)
              </label>
            </div>
            <button type="submit" disabled={saving} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="admin-save-product-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving..." : "Save Product"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/* -------------------- CONTENT EDITORS -------------------- */
function useContent(section) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/content/${section}`).then(({ data }) => setData(data)).catch(() => {});
  }, [section]);
  const save = async (payload) => {
    await api.put(`/content/${section}`, payload);
  };
  return [data, setData, save];
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-muted-foreground">{label}</label>
      <input className="input-field" {...props} />
    </div>
  );
}
function Area({ label, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-muted-foreground">{label}</label>
      <textarea className="input-field resize-none" {...props} />
    </div>
  );
}

function AboutUsPanel() {
  const [data, setData, save] = useContent("about_us");
  const [saving, setSaving] = useState(false);
  if (!data) return <PanelLoader />;

  const setSection = (i, key, val) => {
    const sections = [...(data.sections || [])];
    sections[i] = { ...sections[i], [key]: val };
    setData({ ...data, sections });
  };
  const addSection = () => setData({ ...data, sections: [...(data.sections || []), { title: "", body: "" }] });
  const removeSection = (i) => setData({ ...data, sections: data.sections.filter((_, x) => x !== i) });

  const onSave = async () => {
    setSaving(true);
    try { await save(data); toast.success("About Us saved"); }
    catch (e) { toast.error(apiErr(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-about-us-panel">
      <h2 className="font-serif text-2xl">Edit About Us</h2>
      <Field label="Heading" value={data.heading || ""} onChange={(e) => setData({ ...data, heading: e.target.value })} data-testid="admin-aboutus-heading" />
      <ImageField value={data.image} onChange={(url) => setData({ ...data, image: url })} label="Hero Image" />
      <Area label="Intro" rows={3} value={data.intro || ""} onChange={(e) => setData({ ...data, intro: e.target.value })} data-testid="admin-aboutus-intro" />
      <div className="space-y-4">
        {(data.sections || []).map((s, i) => (
          <div key={i} className="rounded-xl border border-border p-4" data-testid={`admin-aboutus-section-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Section {i + 1}</span>
              <button onClick={() => removeSection(i)} className="text-destructive" data-testid={`admin-aboutus-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <input className="input-field mb-2" placeholder="Title" value={s.title} onChange={(e) => setSection(i, "title", e.target.value)} />
            <textarea className="input-field resize-none" rows={3} placeholder="Body" value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} />
          </div>
        ))}
        <button onClick={addSection} className="btn-outline !py-2" data-testid="admin-aboutus-add-section"><Plus className="h-4 w-4" /> Add Section</button>
      </div>
      <SaveBar onSave={onSave} saving={saving} testid="admin-save-about-us-button" />
    </div>
  );
}

function AboutHomeopathyPanel() {
  const [data, setData, save] = useContent("about_homeopathy");
  const [saving, setSaving] = useState(false);
  if (!data) return <PanelLoader />;

  const setArr = (key, i, field, val) => {
    const arr = [...(data[key] || [])];
    arr[i] = { ...arr[i], [field]: val };
    setData({ ...data, [key]: arr });
  };
  const addItem = (key, obj) => setData({ ...data, [key]: [...(data[key] || []), obj] });
  const removeItem = (key, i) => setData({ ...data, [key]: data[key].filter((_, x) => x !== i) });

  const onSave = async () => {
    setSaving(true);
    try { await save(data); toast.success("About Homeopathy saved"); }
    catch (e) { toast.error(apiErr(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-about-homeopathy-panel">
      <h2 className="font-serif text-2xl">Edit About Homeopathy</h2>
      <Field label="Heading" value={data.heading || ""} onChange={(e) => setData({ ...data, heading: e.target.value })} data-testid="admin-homeo-heading" />
      <ImageField value={data.image} onChange={(url) => setData({ ...data, image: url })} label="Hero Image" />
      <Area label="Intro" rows={3} value={data.intro || ""} onChange={(e) => setData({ ...data, intro: e.target.value })} data-testid="admin-homeo-intro" />

      <div className="space-y-3">
        <h3 className="font-600">Principles</h3>
        {(data.principles || []).map((p, i) => (
          <div key={i} className="rounded-xl border border-border p-4">
            <div className="mb-2 flex justify-end"><button onClick={() => removeItem("principles", i)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></div>
            <input className="input-field mb-2" placeholder="Title" value={p.title} onChange={(e) => setArr("principles", i, "title", e.target.value)} />
            <textarea className="input-field resize-none" rows={2} placeholder="Body" value={p.body} onChange={(e) => setArr("principles", i, "body", e.target.value)} />
          </div>
        ))}
        <button onClick={() => addItem("principles", { title: "", body: "" })} className="btn-outline !py-2"><Plus className="h-4 w-4" /> Add Principle</button>
      </div>

      <div className="space-y-3">
        <h3 className="font-600">FAQs</h3>
        {(data.faqs || []).map((f, i) => (
          <div key={i} className="rounded-xl border border-border p-4">
            <div className="mb-2 flex justify-end"><button onClick={() => removeItem("faqs", i)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></div>
            <input className="input-field mb-2" placeholder="Question" value={f.q} onChange={(e) => setArr("faqs", i, "q", e.target.value)} />
            <textarea className="input-field resize-none" rows={2} placeholder="Answer" value={f.a} onChange={(e) => setArr("faqs", i, "a", e.target.value)} />
          </div>
        ))}
        <button onClick={() => addItem("faqs", { q: "", a: "" })} className="btn-outline !py-2"><Plus className="h-4 w-4" /> Add FAQ</button>
      </div>

      <SaveBar onSave={onSave} saving={saving} testid="admin-save-homeopathy-button" />
    </div>
  );
}

function ContactPanel() {
  const [data, setData, save] = useContent("contact");
  const [saving, setSaving] = useState(false);
  if (!data) return <PanelLoader />;

  const set = (key) => (e) => setData({ ...data, [key]: e.target.value });
  const onSave = async () => {
    setSaving(true);
    try { await save(data); toast.success("Contact details saved"); }
    catch (e) { toast.error(apiErr(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-contact-panel">
      <h2 className="font-serif text-2xl">Edit Contact Details</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Address" value={data.address || ""} onChange={set("address")} data-testid="admin-contact-address" />
        <Field label="Phone" value={data.phone || ""} onChange={set("phone")} data-testid="admin-contact-phone" />
        <Field label="Email" value={data.email || ""} onChange={set("email")} data-testid="admin-contact-email" />
        <Field label="WhatsApp" value={data.whatsapp || ""} onChange={set("whatsapp")} data-testid="admin-contact-whatsapp" />
        <Field label="Clinic Hours" value={data.hours || ""} onChange={set("hours")} data-testid="admin-contact-hours" />
      </div>
      <Area label="Note" rows={3} value={data.note || ""} onChange={set("note")} data-testid="admin-contact-note" />
      <SaveBar onSave={onSave} saving={saving} testid="admin-save-contact-button" />
    </div>
  );
}

function MessagesPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/contact-messages").then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-messages-panel">
      <h2 className="font-serif text-2xl">Enquiries</h2>
      <p className="mb-4 text-sm text-muted-foreground">{rows.length} message(s)</p>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-muted-foreground">No enquiries yet.</p>}
        {rows.map((m) => (
          <div key={m.id} className="rounded-xl border border-border bg-card p-5" data-testid={`admin-message-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-600">{m.name} <span className="font-400 text-muted-foreground">· {m.email}</span></div>
              <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {m.phone && <span>📞 {m.phone} </span>}
              {m.preferred_time && <span>· ⏰ {m.preferred_time}</span>}
            </div>
            {m.concern && <p className="mt-2 text-sm">{m.concern}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function PanelLoader() {
  return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
}
function SaveBar({ onSave, saving, testid }) {
  return (
    <button onClick={onSave} disabled={saving} className="btn-primary disabled:opacity-60" data-testid={testid}>
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
      {saving ? "Saving..." : "Save Changes"}
    </button>
  );
}

/* -------------------- SHELL -------------------- */
export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");

  return (
    <section className="section-pad" data-testid="admin-dashboard">
      <div className="container-xl">
        <div className="mb-8">
          <span className="eyebrow text-accent">Admin Portal</span>
          <h1 className="mt-2 font-serif text-4xl">Dashboard</h1>
          <p className="mt-2 text-muted-foreground">Signed in as {user?.name} ({user?.email})</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          <aside className="flex gap-2 overflow-x-auto lg:flex-col">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition ${
                  tab === t.key ? "bg-primary text-primary-foreground" : "bg-card text-foreground/70 ring-1 ring-border hover:bg-secondary"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </aside>

          <div className="rounded-2xl border border-border bg-card/50 p-6 md:p-8">
            {tab === "products" && <ProductsPanel />}
            {tab === "about_us" && <AboutUsPanel />}
            {tab === "about_homeopathy" && <AboutHomeopathyPanel />}
            {tab === "contact" && <ContactPanel />}
            {tab === "messages" && <MessagesPanel />}
          </div>
        </div>
      </div>
    </section>
  );
}
